/**
 * Capacitor arithmetic: banks in series and parallel, Q = CV and stored energy, and the
 * RC charging and discharging curve. All values are SI base units (farads, volts,
 * coulombs, joules, ohms, seconds).
 */

export type CapVariable = "C" | "V" | "Q" | "E";

export interface CapValues {
  C: number;
  V: number;
  Q: number;
  E: number;
}

export const CAP_PAIRS: readonly [CapVariable, CapVariable][] = [
  ["C", "V"],
  ["C", "Q"],
  ["V", "Q"],
  ["C", "E"],
  ["V", "E"],
  ["Q", "E"],
];

export type CapError = "need-two" | "need-positive";

export type CapResult =
  | { ok: true; values: CapValues }
  | { ok: false; error: CapError };

function has(
  pair: readonly [CapVariable, CapVariable],
  a: CapVariable,
  b: CapVariable
): boolean {
  return (pair[0] === a && pair[1] === b) || (pair[0] === b && pair[1] === a);
}

function fromPair(
  pair: readonly [CapVariable, CapVariable],
  k: Record<CapVariable, number>
): CapValues {
  if (has(pair, "C", "V")) {
    return { C: k.C, V: k.V, Q: k.C * k.V, E: 0.5 * k.C * k.V * k.V };
  }
  if (has(pair, "C", "Q")) {
    return { C: k.C, Q: k.Q, V: k.Q / k.C, E: (k.Q * k.Q) / (2 * k.C) };
  }
  if (has(pair, "V", "Q")) {
    return { V: k.V, Q: k.Q, C: k.Q / k.V, E: 0.5 * k.Q * k.V };
  }
  if (has(pair, "C", "E")) {
    const V = Math.sqrt((2 * k.E) / k.C);
    return { C: k.C, E: k.E, V, Q: k.C * V };
  }
  if (has(pair, "V", "E")) {
    return { V: k.V, E: k.E, C: (2 * k.E) / (k.V * k.V), Q: (2 * k.E) / k.V };
  }
  return { Q: k.Q, E: k.E, C: (k.Q * k.Q) / (2 * k.E), V: (2 * k.E) / k.Q };
}

/** Every quantity above zero: a capacitor with none of C, V, Q or E zero stores energy. */
export function solveCapacitor(
  first: CapVariable,
  second: CapVariable,
  known: Partial<Record<CapVariable, number>>
): CapResult {
  const a = known[first];
  const b = known[second];
  if (a === undefined || b === undefined) {
    return { ok: false, error: "need-two" };
  }
  if (!(a > 0 && b > 0)) {
    return { ok: false, error: "need-positive" };
  }
  const full = { C: 0, V: 0, Q: 0, E: 0, [first]: a, [second]: b };
  return { ok: true, values: fromPair([first, second], full) };
}

export function seriesCapacitance(values: readonly number[]): number {
  return 1 / values.reduce((sum, c) => sum + 1 / c, 0);
}

export function parallelCapacitance(values: readonly number[]): number {
  return values.reduce((sum, c) => sum + c, 0);
}

export interface BankCapacitor {
  farads: number;
  volts: number;
  coulombs: number;
  joules: number;
}

/**
 * Charge, voltage and energy of each capacitor in a bank across `supply` volts. In
 * series all carry the same charge, so the voltage divides in inverse proportion to C;
 * in parallel all share the voltage and the charge divides in proportion to C.
 */
export function analyseBank(
  kind: "series" | "parallel",
  values: readonly number[],
  supply: number
): BankCapacitor[] {
  if (kind === "parallel") {
    return values.map((farads) => ({
      farads,
      volts: supply,
      coulombs: farads * supply,
      joules: 0.5 * farads * supply * supply,
    }));
  }
  const charge = seriesCapacitance(values) * supply;
  return values.map((farads) => {
    const volts = charge / farads;
    return { farads, volts, coulombs: charge, joules: 0.5 * charge * volts };
  });
}

/** The time constant τ = RC, in seconds. */
export function timeConstant(ohms: number, farads: number): number {
  return ohms * farads;
}

/** Capacitor voltage `t` seconds after a step: rising toward `source` or falling from it. */
export function capacitorVoltage(
  source: number,
  tau: number,
  t: number,
  charging: boolean
): number {
  const decay = Math.exp(-t / tau);
  return charging ? source * (1 - decay) : source * decay;
}

/** Circuit current at time t, signed from the source's point of view (A). */
export function rcCurrent(
  source: number,
  ohms: number,
  tau: number,
  t: number
): number {
  return (source / ohms) * Math.exp(-t / tau);
}

/** Time to reach `fraction` of the way to the final value (0 < fraction < 1), in seconds. */
export function timeToFraction(tau: number, fraction: number): number | null {
  if (!(fraction > 0 && fraction < 1)) {
    return null;
  }
  return -tau * Math.log(1 - fraction);
}

/** The −3 dB corner frequency of the same RC as a filter, in hertz. */
export function cornerFrequency(ohms: number, farads: number): number {
  return 1 / (2 * Math.PI * ohms * farads);
}

/** Points of the curve from 0 to `spans` time constants, in seconds and volts. */
export function rcCurve(
  source: number,
  tau: number,
  charging: boolean,
  spans: number,
  steps: number
): [number, number][] {
  const points: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    const t = (spans * tau * i) / steps;
    points.push([t, capacitorVoltage(source, tau, t, charging)]);
  }
  return points;
}
